import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { StateContext } from '../../../../../../StateProvider';
import './ReceptionShuttle.css';

// Reuse whatever icons you already ship
import eventIcon from '../../../../../../../assets/Console/Landing/eventIcon.png';
import timecardIcon from '../../../../../../../assets/Console/Landing/timecardIcon.png';
import petIcon from '../../../../../../../assets/Console/Landing/petIcon.png';
import arrowIcon from '../../../../../../../assets/Landing/arrowIcon.png';

function ReceptionShuttle() {
  var ctx = useContext(StateContext) || {};
  var navigate = useNavigate();

  // If you already hydrate shuttle jobs in context, prefer that.
  // Otherwise we fall back to local demo data for now.
  var incomingJobs = ctx.shuttleJobs;

  var demoJobs = [
    {
      id: 'SJ-1001',
      petName: 'Mochi',
      ownerName: 'Alex Kim',
      phone: '+12135550123',
      address: '1000 W 8th St, Los Angeles, CA 90017',
      neighborhood: 'South Park',
      type: 'Pickup',
      windowStart: '2025-08-15T15:00:00Z',
      windowEnd: '2025-08-15T15:30:00Z',
      status: 'Scheduled',
      driver: 'Unassigned',
      notes: 'Small dachshund. Nervous at doorbell. Text on arrival.',
      jobRef: 'BKG-78421'
    },
    {
      id: 'SJ-1002',
      petName: 'Juniper',
      ownerName: 'Sam Lee',
      phone: '+12135550987',
      address: '801 S Olive St, Los Angeles, CA 90014',
      neighborhood: 'Financial/9th',
      type: 'Dropoff',
      windowStart: '2025-08-15T01:00:00Z',
      windowEnd: '2025-08-15T01:30:00Z',
      status: 'En Route',
      driver: 'Kris',
      notes: 'Has food bag in lobby fridge.',
      jobRef: 'BKG-78498'
    },
    {
      id: 'SJ-1003',
      petName: 'Nova',
      ownerName: 'Taylor Park',
      phone: '+12135557777',
      address: '1200 S Figueroa St, Los Angeles, CA 90015',
      neighborhood: 'L.A. Live',
      type: 'Pickup',
      windowStart: '2025-08-15T18:30:00Z',
      windowEnd: '2025-08-15T19:00:00Z',
      status: 'Scheduled',
      driver: 'Unassigned',
      notes: '',
      jobRef: 'BKG-78512'
    }
  ];

  var initialJobs = Array.isArray(incomingJobs) && incomingJobs.length > 0 ? incomingJobs : demoJobs;

  var _useState1 = useState(initialJobs), jobs = _useState1[0], setJobs = _useState1[1];
  var _useState2 = useState('Today'), dateFilter = _useState2[0], setDateFilter = _useState2[1];
  var _useState3 = useState('All'), statusFilter = _useState3[0], setStatusFilter = _useState3[1];
  var _useState4 = useState('All'), areaFilter = _useState4[0], setAreaFilter = _useState4[1];
  var _useState5 = useState('All'), typeFilter = _useState5[0], setTypeFilter = _useState5[1];
  var _useState6 = useState(''), search = _useState6[0], setSearch = _useState6[1];

  // KPIs at a glance
  var kpis = useMemo(function () {
    var scheduled = 0;
    var enRoute = 0;
    var done = 0;
    var i = 0;
    while (i < jobs.length) {
      var s = jobs[i].status;
      if (s === 'Scheduled' || s === 'Assigned') scheduled = scheduled + 1;
      if (s === 'En Route' || s === 'Picked Up') enRoute = enRoute + 1;
      if (s === 'Completed' || s === 'Dropped Off') done = done + 1;
      i = i + 1;
    }
    return {
      total: jobs.length,
      scheduled: scheduled,
      active: enRoute,
      completed: done
    };
  }, [jobs]);

  // Derived filter options
  var neighborhoods = useMemo(function () {
    var set = {};
    var list = ['All'];
    var i = 0;
    while (i < jobs.length) {
      var nb = jobs[i].neighborhood || 'Unknown';
      if (!set[nb]) {
        set[nb] = true;
        list.push(nb);
      }
      i = i + 1;
    }
    return list;
  }, [jobs]);

  // Simple local filter. Keep logic readable and unchained.
  function visibleJobs() {
    var out = [];
    var i = 0;
    while (i < jobs.length) {
      var j = jobs[i];

      // Date scope – today vs all
      var keepByDate = true;
      if (dateFilter === 'Today') {
        try {
          var now = new Date();
          var ws = new Date(j.windowStart);
          keepByDate = ws.toDateString() === now.toDateString();
        } catch (e) {
          keepByDate = true;
        }
      }

      // Status
      var keepByStatus = true;
      if (statusFilter !== 'All') keepByStatus = j.status === statusFilter;

      // Area
      var keepByArea = true;
      if (areaFilter !== 'All') keepByArea = j.neighborhood === areaFilter;

      // Type
      var keepByType = true;
      if (typeFilter !== 'All') keepByType = j.type === typeFilter;

      // Search
      var keepBySearch = true;
      if (search && search.trim().length > 0) {
        var s = search.toLowerCase();
        var str = (j.id + ' ' + j.petName + ' ' + j.ownerName + ' ' + j.address + ' ' + j.neighborhood + ' ' + j.jobRef).toLowerCase();
        keepBySearch = str.indexOf(s) >= 0;
      }

      if (keepByDate && keepByStatus && keepByArea && keepByType && keepBySearch) out.push(j);
      i = i + 1;
    }
    return out;
  }

  // Helpers
  function fmtWindow(a, b) {
    try {
      var o1 = new Date(a);
      var o2 = new Date(b);
      var fmt = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
      return fmt.format(o1) + ' to ' + fmt.format(o2);
    } catch (e) {
      return '—';
    }
  }

  function phoneHref(raw) {
    if (!raw) return '#';
    return 'tel:' + raw.replace(/[^\d+]/g, '');
  }

  function smsHref(raw, id) {
    if (!raw) return '#';
    var clean = raw.replace(/[^\d+]/g, '');
    var msg = encodeURIComponent('Hi, this is Pawsible Shuttle confirming ' + id + '. Reply YES to confirm.');
    return 'sms:' + clean + '?&body=' + msg;
  }

  function mapHref(addr) {
    if (!addr) return '#';
    return 'https://maps.google.com/?q=' + encodeURIComponent(addr);
  }

  function badgeClass(status) {
    if (status === 'Scheduled') return 'status-badge scheduled';
    if (status === 'Assigned') return 'status-badge assigned';
    if (status === 'En Route') return 'status-badge enroute';
    if (status === 'Picked Up') return 'status-badge picked';
    if (status === 'Dropped Off' || status === 'Completed') return 'status-badge done';
    if (status === 'Cancelled' || status === 'No Show') return 'status-badge warn';
    return 'status-badge';
  }

  // Actions – replace with API calls later, then refresh from server
  function updateStatus(id, next, extra) {
    setJobs(function (prev) {
      var copy = [];
      var i = 0;
      while (i < prev.length) {
        var j = prev[i];
        if (j.id === id) {
          var updated = Object.assign({}, j, { status: next });
          if (extra && extra.driver) updated.driver = extra.driver;
          copy.push(updated);
        } else {
          copy.push(j);
        }
        i = i + 1;
      }
      return copy;
    });
  }

  function assignDriver(id, driverName) {
    setJobs(function (prev) {
      var copy = [];
      var i = 0;
      while (i < prev.length) {
        var j = prev[i];
        if (j.id === id) {
          var s = j.status === 'Scheduled' ? 'Assigned' : j.status;
          copy.push(Object.assign({}, j, { driver: driverName, status: s }));
        } else {
          copy.push(j);
        }
        i = i + 1;
      }
      return copy;
    });
  }

  function goToBooking(jobRef) {
    if (!jobRef) return;
    navigate('/console/bookings/' + jobRef);
    try { window.scrollTo(0, 0); } catch (e) {}
  }

  // Simple driver list; swap for ctx.drivers later
  var drivers = ['Unassigned', 'Kris', 'Jordan', 'Taylor', 'Alex'];

  useEffect(function () {
    // Example: hydrate from ctx when it changes
    if (Array.isArray(incomingJobs) && incomingJobs.length > 0) {
      setJobs(incomingJobs);
    }
  }, [incomingJobs]);

  var list = visibleJobs();

  return (
    <div className="reception-dashboard-container">
      <h4 className="console-header">Reception • Shuttle</h4>
      <h4 className="console-message">Keep pickups prompt, dropoffs smooth, comms warm.</h4>

      {/* KPI Strip */}
      <div className="staff-section-wrapper">
        <div className="staff-section-title">
          <span>🚐</span> Shuttle Brief
        </div>
        <div className="staff-section-grid">
          <div className="brief-card">Jobs Today: <strong>{kpis.total}</strong></div>
          <div className="brief-card">Scheduled: <strong>{kpis.scheduled}</strong></div>
          <div className="brief-card">Active: <strong>{kpis.active}</strong></div>
          <div className="brief-card">Completed: <strong>{kpis.completed}</strong></div>
        </div>
      </div>

      {/* Filters */}
      <div className="shuttle-filters">
        <div className="filter-row">
          <div className="filter-item">
            <label>Date</label>
            <select
              value={dateFilter}
              onChange={function (e) { setDateFilter(e.target.value); }}
            >
              <option value="Today">Today</option>
              <option value="All">All</option>
            </select>
          </div>

          <div className="filter-item">
            <label>Status</label>
            <select
              value={statusFilter}
              onChange={function (e) { setStatusFilter(e.target.value); }}
            >
              <option value="All">All</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Assigned">Assigned</option>
              <option value="En Route">En Route</option>
              <option value="Picked Up">Picked Up</option>
              <option value="Dropped Off">Dropped Off</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
              <option value="No Show">No Show</option>
            </select>
          </div>

          <div className="filter-item">
            <label>Area</label>
            <select
              value={areaFilter}
              onChange={function (e) { setAreaFilter(e.target.value); }}
            >
              {neighborhoods.map(function (n) {
                return <option key={n} value={n}>{n}</option>;
              })}
            </select>
          </div>

          <div className="filter-item">
            <label>Type</label>
            <select
              value={typeFilter}
              onChange={function (e) { setTypeFilter(e.target.value); }}
            >
              <option value="All">All</option>
              <option value="Pickup">Pickup</option>
              <option value="Dropoff">Dropoff</option>
            </select>
          </div>

          <div className="filter-item grow">
            <label>Search</label>
            <input
              value={search}
              placeholder="Pet, owner, job ID, address…"
              onChange={function (e) { setSearch(e.target.value); }}
            />
          </div>
        </div>
      </div>

      {/* List */}
      <div className="shuttle-list">
        {list.length === 0 && (
          <div className="empty-state">
            <img src={timecardIcon} alt="empty" />
            <p>No shuttle jobs match your filters.</p>
          </div>
        )}

        {list.map(function (j) {
          return (
            <div key={j.id} className="shuttle-card">
              <div className="shuttle-card-col main">
                <div className="title-row">
                  <div className="id">{j.id}</div>
                  <div className={badgeClass(j.status)}>{j.status}</div>
                </div>

                <div className="meta">
                  <div className="line">
                    <img src={petIcon} alt="pet" />
                    <strong>{j.petName}</strong> • {j.ownerName}
                  </div>
                  <div className="line">
                    <img src={eventIcon} alt="clock" />
                    <span>{j.type}</span> • <span>{fmtWindow(j.windowStart, j.windowEnd)}</span>
                  </div>
                  <div className="line">
                    <a className="link" href={mapHref(j.address)} target="_blank" rel="noreferrer">
                      {j.address}
                    </a>
                    <span className="pill">{j.neighborhood || 'Area'}</span>
                  </div>
                  {j.notes && <div className="notes">Notes: {j.notes}</div>}
                </div>
              </div>

              <div className="shuttle-card-col side">
                <div className="driver-select">
                  <label>Driver</label>
                  <select
                    value={j.driver || 'Unassigned'}
                    onChange={function (e) { assignDriver(j.id, e.target.value); }}
                  >
                    {drivers.map(function (d) {
                      return <option key={d} value={d}>{d}</option>;
                    })}
                  </select>
                </div>

                <div className="actions">
                  {/* Call and SMS */}
                  <a className="btn ghost" href={phoneHref(j.phone)}>Call</a>
                  <a className="btn ghost" href={smsHref(j.phone, j.id)}>SMS</a>

                  {/* Status advance buttons */}
                  {j.status === 'Scheduled' && (
                    <button className="btn" onClick={function () { updateStatus(j.id, 'Assigned'); }}>
                      Mark Assigned
                    </button>
                  )}
                  {(j.status === 'Scheduled' || j.status === 'Assigned') && (
                    <button className="btn" onClick={function () { updateStatus(j.id, 'En Route'); }}>
                      En Route
                    </button>
                  )}
                  {j.status === 'En Route' && j.type === 'Pickup' && (
                    <button className="btn" onClick={function () { updateStatus(j.id, 'Picked Up'); }}>
                      Picked Up
                    </button>
                  )}
                  {j.status !== 'Completed' && (
                    <button className="btn success" onClick={function () {
                      var next = j.type === 'Dropoff' ? 'Completed' : 'Dropped Off';
                      updateStatus(j.id, next);
                    }}>
                      Complete
                    </button>
                  )}

                  <button className="btn linkish" onClick={function () { goToBooking(j.jobRef); }}>
                    Booking
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Impact / CTA strip reuse */}
      <div className="container">
        <Link to="/store/deals/impact" className="impact-highlight">
          <div className="impact-highlight-card">
            <div className="impact-highlight-card-items">
              <h3 className="impact-highlight-number">Shuttle</h3>
              <h3 className="impact-highlight-paragraph">
                Door to door, stress free. Make every handoff feel premium.
              </h3>
              <div className="impact-highlight-action">
                <h3 className="impact-highlight-button-title">SEE OUR IMPACT</h3>
                <img src={arrowIcon} className="impact-highlight-button" alt="see impact" />
              </div>
            </div>
            <div className="impact-highlight-card-items">
              <img className="impact-highlight-icon" src={timecardIcon} alt="shuttle icon" />
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
}

export default ReceptionShuttle;
